import React, { useState, useContext, useEffect } from 'react';
import classNames from 'classnames';
import { object, string } from 'prop-types';
import TilesCarousel from '../tiles_carousel';
import FilterTile from '../tiles/filter_tile';
import tracker from '../../../service/tracker';
import { EVENTS_MAPPING } from '../../../utils/constants';
import { AppContext } from '../../context/listingsContext';
import { useDidMount } from '../../../hooks';
import { MINIMUN_TILES_LENGTH } from '../tiles_carousel/constants';
import Title from './title';
import SkipButton from './skipButton';
import { reorderList, findStepIndex, isNextStepExists } from './utils';
import { FADE_ANIMATION_DURATION, TILE_SIZES, VIEW_ALL_COMPONENT } from './constants';
import './index.scss';

export const CarouselContext = React.createContext();

const TopFiltersCarousel = ({ topFiltersCarousel, subCategoryId }) => {
    const { filter: { active: activeFilters, topBar: availableFilters }, isTouch } = useContext(AppContext);
    const activeFiltersKeys = Object.keys(activeFilters);
    const { id: carouselId, filterTiles } = topFiltersCarousel;
    const availableFiltersIdList = availableFilters.map(({ id }) => id);
    const initStepIndex = findStepIndex(filterTiles, activeFiltersKeys, availableFiltersIdList);
    const [stepIndex, setStepIndex] = useState(initStepIndex);
    const [collapseCarousel, setCollapseCarousel] = useState(false);
    const [fadeCarousel, setfadeCarousel] = useState(false);
    const [showBackButton, setShowBackButton] = useState(false);
    const didMount = useDidMount();
    const allFilterTilesAreSelected = stepIndex === -1;

    useEffect(() => {
        if (didMount) {
            setfadeCarousel(true);
            setTimeout(() => {
                setfadeCarousel(false);
            }, FADE_ANIMATION_DURATION);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepIndex]);

    const { id: filterId, tilesOrder = [], tileSize = TILE_SIZES.MEDIUM } = filterTiles[stepIndex] || {};
    const filterIndex = availableFilters.findIndex((el) => el.id === filterId);
    const { options: availableFilterOptions = [] } = availableFilters[filterIndex] || {};
    const orderedList = reorderList(tilesOrder, availableFilterOptions, stepIndex);

    if (allFilterTilesAreSelected || orderedList.length < MINIMUN_TILES_LENGTH) {
        return null;
    }

    const nextFilter = filterTiles[stepIndex + 1] || {};
    const isNextFilterSelected = activeFiltersKeys.includes(nextFilter.id);
    const hasNextStep = isNextStepExists(nextFilter, availableFilters, isNextFilterSelected);
    const stepNum = stepIndex + 1;
    const isLastStep = !hasNextStep;
    const totalSteps = filterTiles.filter(({ id }) => availableFiltersIdList.includes(id)).length;
    const showViewAllTile = availableFilterOptions.length > orderedList.length;

    showViewAllTile && orderedList.push({ id: VIEW_ALL_COMPONENT });

    const carouselContext = {
        filterId,
        carouselId,
        subCategoryId,
        stepIndex,
        allFilters: availableFilterOptions,
        stepNum,
        isLastStep,
        tileSize,
        setCollapseCarousel,
        setStepIndex
    };

    return (
        <div className={classNames('top-filters-carousel',
            { 'fade-in': fadeCarousel }, { 'collapsed': collapseCarousel })}>
            <CarouselContext.Provider value={{ ...carouselContext }}>
                <Title totalSteps={totalSteps}
                    isNextFilterSelected={isNextFilterSelected}
                    setShowBackButton={setShowBackButton}
                    showBackButton={showBackButton}/>
                <div className="top-filters-carousel-wrapper">
                    <TilesCarousel items={orderedList}
                        isTouch={isTouch}
                        TileComponent={FilterTile}
                        onArrowClick={() => tracker.trackTileFilter(EVENTS_MAPPING.FILTERS_ARROW, filterId, stepNum)}
                        showSkipButton={hasNextStep}/>
                    <SkipButton showSkipButton={hasNextStep} setShowBackButton={setShowBackButton}/>
                </div>
            </CarouselContext.Provider>
        </div>
    );
};

TopFiltersCarousel.propTypes = {
    topFiltersCarousel: object,
    subCategoryId: string
};

export default TopFiltersCarousel;
